import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import * as S from './RelatedAds.styles';
import { Ad } from '@/api/types/common.types';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useToastStore } from '@/store/useToastStore';
import { useResponsive } from '@/hooks/useResponsive';
import { AdCardNew } from '@/components/AdCardNew';
import { MAX_RELATED_ADS } from '@/views/Ads/Ad.view.constants';

const CollectionMasterModal = dynamic(
  () => import('@/features').then(mod => mod.CollectionMasterModal),
  { ssr: false }
);

const CollectionMasterSheet = dynamic(
  () => import('@/features').then(mod => mod.CollectionMasterSheet),
  { ssr: false }
);

const Toast = dynamic(
  () => import('@/components/UI/Toast').then(mod => mod.Toast),
  { ssr: false }
);

interface Props {
  isLoading: boolean;
  title: string;
  ads?: Ad[];
  showAllLink?: string;
  chunkSize: AdsChunkSize;
}

export const RelatedAds: FC<Props> = props => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const { openToast, closeToast, onAnimationComplete } = useToastStore();
  const { title, isLoading, ads = [], showAllLink, chunkSize } = props;

  const adsList = ads.slice(0, MAX_RELATED_ADS);

  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);

  const handleAddToFavorites = (targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.toast_tip'),
        action: t('bookmark_collections.toast_action'),
        onActionClick: handleToastActionClick,
      });
    }, 50);
  };

  const handleRemoveFromFavorites = (targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.unbookmarked'),
      });
    }, 50);
  };

  const handleToastActionClick = () => {
    closeToast();
    setIsCollectionMasterOpen(true);
  };

  return (
    <>
      <S.Container data-testid={CommonTestIds.adsSection.section}>
        <S.TitleContainer>
          <S.Title data-testid={CommonTestIds.adsSection.title}>
            {isLoading ? <SkeletonLoader width="125px" height="18px" /> : title}
          </S.Title>
          {showAllLink && (
            <S.ShowAllLink
              href={showAllLink}
              dataTestId={CommonTestIds.adsSection.showAll}
            >
              {t(`common.${isDesktop ? 'show_all' : 'all'}`)}
            </S.ShowAllLink>
          )}
        </S.TitleContainer>
        <S.AdCardsBox data-testid={CommonTestIds.adCards.cardsBox}>
          {isLoading
            ? Array.from({ length: chunkSize }, (_, index) => (
                <AdCardNew key={index} />
              ))
            : adsList.map((ad, idx) => {
                const key = `${ad.id}_${idx}`;
                return (
                  <AdCardNew
                    key={key}
                    ad={ad}
                    onAddBookmark={handleAddToFavorites}
                    onRemoveBookmark={handleRemoveFromFavorites}
                  />
                );
              })}
        </S.AdCardsBox>
      </S.Container>

      {isDesktop ? (
        <CollectionMasterModal
          isOpen={isCollectionMasterOpen}
          onClose={() => setIsCollectionMasterOpen(false)}
        />
      ) : (
        <CollectionMasterSheet
          isOpen={isCollectionMasterOpen}
          onClose={() => setIsCollectionMasterOpen(false)}
        />
      )}

      <Toast />
    </>
  );
};
